"use client"

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Upload, Download, Eye, FileText, Image, FileIcon, X } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function FileUploadField({
  id,
  label,
  required,
  multiple = false,
  accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png",
  value = [],
  onChange,
  disabled,
  style,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = React.useRef(null);
  const files = Array.isArray(value) ? value : [];

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFiles = async (fileList) => {
    try {
      const newFiles = await Promise.all(
        Array.from(fileList).map(async (file) => {
          const base64 = await fileToBase64(file);
          return {
            id: Date.now() + Math.random().toString(36).substring(2, 9),
            name: file.name,
            size: file.size,
            type: file.type,
            base64: base64,
            uploadedAt: new Date().toISOString(),
          };
        })
      );

      if (multiple) {
        onChange([...files, ...newFiles]);
      } else {
        onChange(newFiles.slice(0, 1));
      }
    } catch (error) {
      console.error("Error converting files to base64:", error);
      // You might want to show a toast notification here
    }
  };

  const removeFile = (fileId) => {
    onChange(files.filter((file) => file.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return <Image className="h-5 w-5 text-blue-500" />;
    } else if (fileType === 'application/pdf') {
      return <FileText className="h-5 w-5 text-red-500" />;
    } else if (fileType.includes('document') || fileType.includes('word')) {
      return <FileText className="h-5 w-5 text-blue-600" />;
    } else {
      return <FileIcon className="h-5 w-5 text-slate-500" />;
    }
  };

  const downloadFile = (file) => {
    const link = document.createElement('a');
    link.href = file.base64;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openPreview = (file) => {
    setPreviewFile(file);
    setShowPreview(true);
  };

  
  const renderFileCard = (file) => (
    <motion.div
      key={file.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 shadow-sm"
    >
      {/* Diseño horizontal compacto */}
      <div className="flex items-center justify-between">
        {/* Información del archivo */}
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          {getFileIcon(file.type)}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
              {file.name}
            </p>
            <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400">
              <span>{formatFileSize(file.size)}</span>
              {file.uploadedAt && (
                <>
                  <span>•</span>
                  <span>
                    {new Date(file.uploadedAt).toLocaleDateString('es-ES', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex items-center space-x-2 ml-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openPreview(file)}
            className="text-blue-600 hover:text-blue-700 border-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 relative"
            style={{ zIndex: 50 }}
          >
            <Eye className="h-4 w-4 mr-1" />
            Ver
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => downloadFile(file)}
            className="text-green-600 hover:text-green-700 border-green-200 hover:bg-green-50 dark:hover:bg-green-900/20"
          >
            <Download className="h-4 w-4 mr-1" />
            Descargar
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={() => removeFile(file.id)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-4">
      {/* Área de drop/upload */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
          isDragging
            ? "border-orange-400 bg-orange-50 dark:bg-orange-900/20"
            : "border-slate-300 dark:border-slate-600 hover:border-orange-300 dark:hover:border-orange-500"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          id={id}
          required={required && files.length === 0}
          multiple={multiple}
          accept={accept}
          className="hidden"
          onChange={handleFileInputChange}
          disabled={disabled}
        />

        <div className="flex flex-col items-center justify-center space-y-3">
          <div className="rounded-full bg-orange-100 dark:bg-orange-900/30 p-4">
            <Upload className="h-8 w-8 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            <span className="font-medium text-slate-900 dark:text-slate-100">Haz clic para subir</span> o arrastra
            y suelta aquí
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm">
            {multiple ? "Archivos soportados" : "Archivo soportado"}: {accept.split(",").map(ext => ext.trim()).join(", ")}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="btn-clean btn-primary"
            disabled={disabled}
          >
            <Upload className="h-4 w-4 mr-2" />
            Seleccionar {multiple ? "archivos" : "archivo"}
          </Button>
        </div>
      </div>

      {/* Lista de archivos cargados */}
      <AnimatePresence>
      {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {multiple ? `Archivos cargados (${files.length})` : "Archivo cargado"}
              </h4>
              {files.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    files.forEach(file => downloadFile(file));
                  }}
                  className="text-green-600 hover:text-green-700 border-green-200 hover:bg-green-50"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Descargar todos
                </Button>
              )}
            </div>

            <div className={`grid gap-4 ${multiple && files.length > 1 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
              <AnimatePresence>
                {files.map(renderFileCard)}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de preview */}
      {showPreview && previewFile && (
        typeof window !== 'undefined' && document.body ? 
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
              style={{ zIndex: 99999 }}
              onClick={() => setShowPreview(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-slate-900 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-700"
                onClick={(e) => e.stopPropagation()}
                style={{ zIndex: 100000 }}
              >
                {/* Header del modal */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                      {getFileIcon(previewFile.type)}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 truncate max-w-md">
                        {previewFile.name}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {formatFileSize(previewFile.size)} • {previewFile.type.split('/')[1]?.toUpperCase() || 'Archivo'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadFile(previewFile)}
                      className="btn-clean btn-secondary"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Descargar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPreview(false)}
                      className="hover:bg-slate-200 dark:hover:bg-slate-700"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {/* Contenido del preview */}
                <div className="p-6 max-h-[calc(90vh-120px)] overflow-auto bg-white dark:bg-slate-900">
                  {previewFile.type.startsWith('image/') ? (
                    <div className="flex items-center justify-center">
                      <img 
                        src={previewFile.base64} 
                        alt={previewFile.name}
                        className="max-w-full max-h-[600px] object-contain rounded-lg shadow-md"
                      />
                    </div>
                  ) : previewFile.type === 'application/pdf' ? (
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="inline-flex items-center space-x-2 bg-red-50 dark:bg-red-900/20 px-4 py-2 rounded-lg">
                          <FileText className="h-5 w-5 text-red-600 dark:text-red-400" />
                          <span className="text-sm font-medium text-red-700 dark:text-red-300">
                            Archivo PDF - Vista previa
                          </span>
                        </div>
                      </div>
                      
                      <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-8 text-center space-y-4">
                        <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
                          <FileText className="h-10 w-10 text-red-600 dark:text-red-400" />
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            Documento PDF
                          </h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                            Para ver el contenido completo del PDF, descarga el archivo o ábrelo en una nueva ventana.
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <Button
                            onClick={() => downloadFile(previewFile)}
                            className="btn-clean btn-primary"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Descargar PDF
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              window.open(previewFile.base64, '_blank');
                            }}
                            className="btn-clean btn-secondary"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Abrir en nueva ventana
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : previewFile.type.includes('spreadsheet') || previewFile.type.includes('excel') || previewFile.name.endsWith('.xlsx') || previewFile.name.endsWith('.xls') ? (
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="inline-flex items-center space-x-2 bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-lg">
                          <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
                          <span className="text-sm font-medium text-green-700 dark:text-green-300">
                            Archivo Excel
                          </span>
                        </div>
                      </div>

                      <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-8 text-center space-y-4">
                        <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                          <FileText className="h-10 w-10 text-green-600 dark:text-green-400" />
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            Hoja de Cálculo Excel
                          </h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                            Descarga el archivo para abrirlo en Excel o tu aplicación de hojas de cálculo preferida.
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <Button
                            onClick={() => downloadFile(previewFile)}
                            className="btn-clean btn-primary"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Descargar Excel
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = previewFile.base64;
                              link.target = '_blank';
                              link.download = previewFile.name;
                              link.click();
                            }}
                            className="btn-clean btn-secondary"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Abrir archivo
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : previewFile.type.includes('document') || previewFile.type.includes('word') || previewFile.name.endsWith('.docx') || previewFile.name.endsWith('.doc') ? (
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="inline-flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-lg">
                          <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            Documento Word
                          </span>
                        </div>
                      </div>

                      <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-8 text-center space-y-4">
                        <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto">
                          <FileText className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            Documento de Word
                          </h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                            Descarga el archivo para abrirlo en Microsoft Word o tu procesador de texto preferido.
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <Button
                            onClick={() => downloadFile(previewFile)}
                            className="btn-clean btn-primary"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Descargar Word
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = previewFile.base64;
                              link.target = '_blank';
                              link.download = previewFile.name;
                              link.click();
                            }}
                            className="btn-clean btn-secondary"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Abrir archivo
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="inline-flex items-center space-x-2 bg-slate-50 dark:bg-slate-800 px-4 py-2 rounded-lg">
                          <FileIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Archivo adjunto
                          </span>
                        </div>
                      </div>

                      <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-8 text-center space-y-4">
                        <div className="w-20 h-20 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto">
                          <FileIcon className="h-10 w-10 text-slate-500" />
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            Archivo adjunto
                          </h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                            Este tipo de archivo no tiene vista previa disponible. Descárgalo para verlo en la aplicación correspondiente.
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <Button
                            onClick={() => downloadFile(previewFile)}
                            className="btn-clean btn-primary"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Descargar archivo
                </Button>
              </div>
          </div>
        </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        ) : (
          // Fallback en caso de que document.body no esté disponible
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
              style={{ zIndex: 99999 }}
              onClick={() => setShowPreview(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-slate-900 rounded-xl max-w-lg w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center">
                  <p className="text-slate-700 dark:text-slate-300 mb-4">
                    Modal no disponible en este contexto. 
                  </p>
                  <Button onClick={() => downloadFile(previewFile)}>
                    Descargar archivo
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        )
      )}
    </div>
  );
}

